import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router'
import RestaurantDishesCard from '../../../../components/restaurant/RestaurantDishesCard'
import RestaurantReview from '../../../../components/restaurant/RestaurantReview'
import InputComponent from '../../../../components/ui/InputComponent'
import useAppUtilities from '../../../../hooks/useAppUtilities'
import { useAppSelector } from '../../../../store'
import {
  selectEater,
  selectEaterDishReviews,
  setEaterDishReviews,
  setRestaurantReviews,
} from '../../../../store/profileSlice'
import {
  deleteRestaurantReview,
  fetchRestaurantReviewsByAlias,
  saveAnonRestaurantReview,
  saveRestaurantReviewsByAlias,
  selectRestaurantDetail,
  selectRestaurantDishList,
  setMessage,
  setOpen,
  setSeverity,
  updateRestaurantReviewsByAlias,
} from '../../../../store/restaurantSlice'
import {
  IRestaurantReview,
  IRestaurantSentPayloadReview,
} from '../../../../types'
import { useAuth0 } from '@auth0/auth0-react'
import { logEvent } from '../../../../analytics/analytics'
import { ensureAnonId } from '../../../../helpers/anon-id'
import { PredictedDish } from '../../../../types/menuTypes'
import {
  RestaurantListDishInnerWrapper,
  RestaurantListDishWrapper,
  ReviewsTabDishReviewTypography,
  ReviewsTabInnerGrid,
  ReviewsTabMainContainer,
  ReviewsTabMainGrid,
  ReviewsTabTypography,
} from '../styled/ReviewsTab.styled'

const ReviewsTab: React.FC = () => {
  const { location, dispatch } = useAppUtilities()
  const { alias } = useParams()
  const userDetail = useAppSelector(selectEater)
  const restaurantDishList = useAppSelector(selectRestaurantDishList)
  const saveRestaurantDishList = useAppSelector(selectEaterDishReviews)
  const restaurant = useAppSelector(selectRestaurantDetail)?.restaurantDTO

  const queryParams = new URLSearchParams(location.search)
  const tabMode = queryParams.get('mode_type')

  const [dishSearch, setDishSearch] = useState('')
  const [allReviewedDishes, setAllReviewedDishes] = useState<PredictedDish[]>(
    [],
  )
  const [dish, setDish] = useState<PredictedDish[]>([])
  const [restaurantReviewLoader, setRestaurantReviewLoader] =
    useState<boolean>(false)
  const [restaurantReview, setRestaurantReview] = useState<
    IRestaurantReview | undefined
  >(undefined)
  const [reviewMode, setReviewMode] = useState<'readonly' | 'edit' | 'new'>(
    'new',
  )
  const { isAuthenticated } = useAuth0()

  // Track a started-but-not-submitted review so we can emit review_abandoned on exit.
  // "started" now means genuine user input (not merely opening the form), so it doubles
  // as the once-per-open guard for review_started.
  const reviewStartedRef = useRef(false)
  const reviewSubmittedRef = useRef(false)

  // A newly opened write-form is a clean slate: no input yet, nothing to abandon.
  useEffect(() => {
    if (reviewMode === 'new' || reviewMode === 'edit') {
      reviewStartedRef.current = false
      reviewSubmittedRef.current = false
    }
  }, [reviewMode])

  // Fire review_started on the FIRST genuine interaction per form-open; dedupe via reviewStartedRef.
  const handleReviewInteract = (): void => {
    if (reviewMode !== 'new' && reviewMode !== 'edit') return
    if (reviewStartedRef.current) return
    reviewStartedRef.current = true
    reviewSubmittedRef.current = false
    logEvent('review_started', {
      restaurant_id: restaurant?.restaurantId ?? restaurant?.alias ?? '',
      screen_name: 'restaurant_detail',
    })
  }

  // On unmount: if a review was started but never submitted, it was abandoned.
  useEffect(() => {
    return () => {
      if (reviewStartedRef.current && !reviewSubmittedRef.current) {
        logEvent('review_abandoned', {
          restaurant_id: restaurant?.restaurantId ?? restaurant?.alias ?? '',
          screen_name: 'restaurant_detail',
        })
      }
    }
  }, [restaurant])

  const onDishSearchChange = (search: string): void => {
    setDishSearch(search)
    const base = allReviewedDishes ?? []
    const searchDishList =
      search.length > 0
        ? base.filter((d) =>
            d.dish.name.toLowerCase().includes(search.toLowerCase()),
          )
        : base
    setDish(searchDishList)
  }

  useEffect(() => {
    if (!restaurantDishList?.length || !saveRestaurantDishList?.length) {
      setAllReviewedDishes([])
      setDish([])
      return
    }

    const reviewedDishes = restaurantDishList
      .map((predDish) => {
        const review = saveRestaurantDishList.find(
          (r) =>
            r.dishId === predDish.dish.dishID ||
            (r as any).dish?.dishID === predDish.dish.dishID,
        )
        if (!review) return null

        return {
          ...predDish,
          dish: {
            ...predDish.dish,
            // name stays the same
            description:
              review.sentimentSummary && review.sentimentSummary.length > 0
                  ? review.sentimentSummary
                  : predDish.dish.description,
            starCount: review.starCount ?? 0,
          },
        }
      })
      .filter((d): d is PredictedDish => d !== null)

    setAllReviewedDishes(reviewedDishes)
    setDish(reviewedDishes)
  }, [restaurantDishList, saveRestaurantDishList, alias])

  // Fetch latest reviews when tab opens or alias changes
  useEffect(() => {
    // The eater's own reviews are auth-scoped (GET /reviews/{alias}/dining-review requires auth);
    // anonymous users have none, so skip the fetch to avoid a spurious 401 on every logged-out
    // tab open. Anonymous submit still works via the public /reviews/public/** endpoint.
    if (!alias || !isAuthenticated) return

    // Fetch latest reviews for this eater + restaurant
    const doFetch = async (): Promise<void> => {
      const result = await dispatch(fetchRestaurantReviewsByAlias({ alias }))

      if (fetchRestaurantReviewsByAlias.fulfilled.match(result)) {
        const payload = result.payload

        // Update profile slice with fresh data
        if (payload.restaurantReview) {
          // Merge into restaurantReviews list
          // Read current state at the time of update
          const existing = userDetail?.restaurantReviews ?? []
          const other = existing.filter(
            (r) =>
              r.restaurantAlias !== payload.restaurantReview?.restaurantAlias,
          )
          const updated = payload.restaurantReview
            ? [...other, payload.restaurantReview]
            : other

          dispatch(setRestaurantReviews(updated))
        }

        // Only update dishReviews if payload explicitly provides them
        // Do NOT clear existing dishReviews if payload is missing this field
        if (payload.dishReviews !== undefined && payload.dishReviews !== null) {
          dispatch(setEaterDishReviews(payload.dishReviews))
        }
        // If payload.dishReviews is undefined/null, preserve existing state.eaterDishReviews
      }
    }

    void doFetch()
  }, [alias, dispatch, isAuthenticated])

  useEffect(() => {
    const review = userDetail?.restaurantReviews?.find(
      (review) => review.restaurantAlias === alias,
    )
    if (review) {
      setReviewMode('readonly')
    } else {
      setReviewMode('new')
    }
    setRestaurantReview(review)
  }, [userDetail?.restaurantReviews, alias])

  useEffect(() => {
    if (tabMode === 'edit') {
      setReviewMode('edit')
    }
  }, [])

  const onReviewUpdate = async (
    data: IRestaurantSentPayloadReview,
  ): Promise<void> => {
    setRestaurantReviewLoader(true)

    const payload: IRestaurantSentPayloadReview = {
      starCount: data.starCount ?? 0,
      value: data.value ?? '',
      alias: restaurant?.alias ?? '',
      images: data.images ?? [],
      deleted: data.deleted ?? [],
      likesCuisine: data.likesCuisine,
      likesAmbience: data.likesAmbience,
      likesStaff: data.likesStaff,
      likesWaitingTime: data.likesWaitingTime,
      likesPrice: data.likesPrice,
      likesMenu: data.likesMenu,
      wouldReturn: data.wouldReturn,
    }

    const aspectCount = [
      data.likesMenu,
      data.likesStaff,
      data.likesAmbience,
      data.likesPrice,
      data.likesWaitingTime,
    ].filter((v) => v !== undefined && v !== null).length

    const emitSubmitted = (isAnon: boolean): void => {
      reviewSubmittedRef.current = true
      logEvent('review_submitted', {
        restaurant_id: restaurant?.restaurantId ?? restaurant?.alias ?? '',
        rating: payload.starCount ?? 0,
        aspect_count: aspectCount,
        has_text: !!payload.value,
        has_photo: (payload.images?.length ?? 0) > 0,
        would_return:
          payload.wouldReturn === undefined
            ? 'unanswered'
            : payload.wouldReturn
              ? 'yes'
              : 'no',
        is_anon: isAnon,
        screen_name: 'restaurant_detail',
      })
    }

    // Anonymous: store via the public endpoint with the device anon id; claimed + scored on signup.
    if (!isAuthenticated) {
      const anonId = await ensureAnonId()
      const result = await dispatch(
        saveAnonRestaurantReview({ ...payload, anonId }),
      )
      if (saveAnonRestaurantReview.fulfilled.match(result)) {
        emitSubmitted(true)
        dispatch(
          setMessage(
            'Thanks! Create an account to save your review and earn points.',
          ),
        )
        dispatch(setSeverity('success'))
        dispatch(setOpen(true))
        setReviewMode('readonly')
      }
      setRestaurantReviewLoader(false)
      return
    }

    if (restaurantReview && restaurantReview.restaurantReviewId?.length) {
      const result = await dispatch(updateRestaurantReviewsByAlias(payload))
      if (updateRestaurantReviewsByAlias.fulfilled.match(result)) {
        const responsePayload = result.payload
        
        // Handle EaterRestaurantReviewsResponse consistently
        if (responsePayload.restaurantReview) {
          // Merge restaurantReview into profile slice
          const existing = userDetail?.restaurantReviews ?? []
          const other = existing.filter(
            (r) => r.restaurantAlias !== responsePayload.restaurantReview?.restaurantAlias,
          )
          const updated = [...other, responsePayload.restaurantReview]
          dispatch(setRestaurantReviews(updated))
        }
        
        // Update dishReviews if provided (do not clear if missing)
        if (responsePayload.dishReviews !== undefined && responsePayload.dishReviews !== null) {
          dispatch(setEaterDishReviews(responsePayload.dishReviews))
        }
        
        dispatch(setMessage(`Restaurant review has been update successfully.`))
        dispatch(setSeverity('success'))
        dispatch(setOpen(true))
        setReviewMode('readonly')
        emitSubmitted(false)
        
        // Background refresh to get full data (images, sentiment, etc.) after async job completes
        if (alias) {
          void dispatch(fetchRestaurantReviewsByAlias({ alias }))
        }
      }
    } else {
      const result = await dispatch(saveRestaurantReviewsByAlias(payload))
      if (saveRestaurantReviewsByAlias.fulfilled.match(result)) {
        const responsePayload = result.payload
        
        // Handle EaterRestaurantReviewsResponse consistently
        if (responsePayload.restaurantReview) {
          // Merge restaurantReview into profile slice
          const existing = userDetail?.restaurantReviews ?? []
          const other = existing.filter(
            (r) => r.restaurantAlias !== responsePayload.restaurantReview?.restaurantAlias,
          )
          const updated = [...other, responsePayload.restaurantReview]
          dispatch(setRestaurantReviews(updated))
        }
        
        // Update dishReviews if provided (do not clear if missing)
        if (responsePayload.dishReviews !== undefined && responsePayload.dishReviews !== null) {
          dispatch(setEaterDishReviews(responsePayload.dishReviews))
        }
        
        dispatch(setMessage(`Restaurant review has been saved successfully.`))
        dispatch(setSeverity('success'))
        dispatch(setOpen(true))
        setReviewMode('readonly')
        emitSubmitted(false)
        
        // Background refresh to get full data (images, sentiment, etc.) after async job completes
        if (alias) {
          void dispatch(fetchRestaurantReviewsByAlias({ alias }))
        }
      }
    }
    setRestaurantReviewLoader(false)
  }

  const onReviewAction = async (action: 'edit' | 'delete'): Promise<void> => {
    if (action === 'edit') {
      setReviewMode('edit')
    }
    if (
      action === 'delete' &&
      restaurantReview &&
      restaurantReview.restaurantReviewId
    ) {
      setRestaurantReviewLoader(true)
      const result = await dispatch(
        deleteRestaurantReview(restaurantReview.restaurantReviewId),
      )
      if (deleteRestaurantReview.fulfilled.match(result)) {
        dispatch(setRestaurantReviews(result.payload.restaurantReviews))
        dispatch(setMessage(`Restaurant review has been delete successfully.`))
        dispatch(setSeverity('success'))
        dispatch(setOpen(true))
      }
      setRestaurantReviewLoader(false)
      setReviewMode('new')
    }
  }

  return (
    <div>
      {reviewMode !== 'readonly' && (
        <ReviewsTabTypography>
          {reviewMode === 'edit'
            ? 'Edit Your Review'
            : 'You have been here, How was your experience?'}
        </ReviewsTabTypography>
      )}

      <ReviewsTabMainContainer>
        <ReviewsTabMainGrid>
          <ReviewsTabInnerGrid>
            <RestaurantReview
              review={restaurantReview}
              isLoading={restaurantReviewLoader}
              mode={reviewMode}
              onInteract={handleReviewInteract}
              onReview={(item) => {
                void onReviewUpdate(item)
              }}
              onReviewAction={(name) => {
                void onReviewAction(name)
              }}
            />
          </ReviewsTabInnerGrid>
          {allReviewedDishes.length > 0 && (
          <ReviewsTabInnerGrid>
            <ReviewsTabDishReviewTypography>
              Reviewed Dishes
            </ReviewsTabDishReviewTypography>
            <InputComponent
              id="dishSearch-review"
              value={dishSearch}
              setValue={onDishSearchChange}
              placeholder="Search dish name"
            />
            <RestaurantListDishWrapper>
              {dish.map((item, index) => (
                <RestaurantListDishInnerWrapper key={index}>
                  <RestaurantDishesCard
                    isLoading={false}
                    id={item?.dish?.dishID}
                    image={
                      item?.dish?.imageUrl && item.dish.imageUrl.length !== 0
                        ? item?.dish?.imageUrl
                        : '/logoLetter-resize.svg'
                    }
                    title={item?.dish?.name}
                    description={item?.dish?.description}
                    isEditable={false}
                    rating={item?.dish?.starCount ?? 0}
                    tagChipData={[
                      {
                        percentage: `${item?.probability?.yes.toFixed(2)}`,
                        title: 'Match',
                      },
                    ]}
                  />
                </RestaurantListDishInnerWrapper>
              ))}
            </RestaurantListDishWrapper>
          </ReviewsTabInnerGrid>
          )}
        </ReviewsTabMainGrid>
      </ReviewsTabMainContainer>
    </div>
  )
}

export default ReviewsTab
